import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import { FaEdit, FaTrash, FaPlus, FaUndo, FaStore } from 'react-icons/fa';
import AdminNavbar from '../../components/admin/AdminNavbar';
import Swal from 'sweetalert2';

const CampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const data = await adminService.getCampaigns();
      setCampaigns(data);
    } catch (error) {
      toast.error('Error al cargar las campañas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta campaña?')) {
      try {
        await adminService.deleteCampaign(id);
        toast.success('Campaña eliminada correctamente');
        loadCampaigns();
      } catch (error) {
        toast.error('Error al eliminar la campaña');
      }
    }
  };

  const handleReset = async (campaign) => {
    const result = await Swal.fire({
      title: '¿Resetear campaña?',
      html: `
        <p>Esta acción:</p>
        <ul style="text-align: left; margin-top: 1rem;">
          <li>Eliminará todas las participaciones</li>
          <li>Restaurará el número de premios disponibles</li>
        </ul>
        <p style="margin-top: 1rem;">Esta acción no se puede deshacer.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, resetear',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await adminService.resetCampaign(campaign._id);
        toast.success('Campaña reseteada correctamente');
        loadCampaigns();
      } catch (error) {
        toast.error('Error al resetear la campaña');
      }
    }
  };

  const showBusinessCredentials = async (campaign) => {
    const result = await Swal.fire({
      title: 'Acceso Dashboard Business',
      html: `
        <div style="text-align: left; padding: 1rem;">
          <p style="margin: 0.5rem 0;"><strong>Usuario:</strong> ${campaign.business.username}</p>
          <p style="margin: 0.5rem 0;"><strong>URL:</strong> /business/${campaign.business.adminUrl}</p>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Cerrar',
      denyButtonText: 'Resetear contraseña',
      denyButtonColor: '#EF4444',
      customClass: {
        popup: 'business-credentials-popup',
        title: 'business-credentials-title',
        htmlContainer: 'business-credentials-content',
        confirmButton: 'business-credentials-button',
        denyButton: 'business-credentials-reset-button'
      }
    });

    if (result.isDenied) {
      // Confirmar reset de contraseña
      const confirmReset = await Swal.fire({
        title: '¿Resetear contraseña?',
        text: 'Se generará una nueva contraseña. Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#EF4444',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Sí, resetear',
        cancelButtonText: 'Cancelar'
      });

      if (confirmReset.isConfirmed) {
        try {
          const response = await adminService.resetBusinessPassword(campaign._id);
          
          // Mostrar nuevas credenciales
          await Swal.fire({
            title: 'Nueva contraseña generada',
            html: `
              <div style="text-align: left; padding: 1rem;">
                <p style="margin: 0.5rem 0;"><strong>Usuario:</strong> ${response.credentials.username}</p>
                <p style="margin: 0.5rem 0;"><strong>Contraseña:</strong> ${response.credentials.password}</p>
                <p style="margin: 0.5rem 0;"><strong>URL:</strong> /business/${response.credentials.adminUrl}</p>
              </div>
            `,
            icon: 'success',
            customClass: {
              popup: 'business-credentials-popup',
              title: 'business-credentials-title',
              htmlContainer: 'business-credentials-content',
              confirmButton: 'business-credentials-button'
            }
          });
        } catch (error) {
          toast.error('Error al resetear la contraseña');
        }
      }
    }
  };

  if (loading) {
    return <LoadingMessage>Cargando campañas...</LoadingMessage>;
  }

  return (
    <>
      <AdminNavbar />
      <Container>
        <Header>
          <Title>Campañas</Title>
          <AddButton onClick={() => navigate('/pmpl/admin/campaigns/new')}>
            <FaPlus /> Nueva Campaña
          </AddButton>
        </Header>

        <CampaignsGrid>
          {campaigns.map(campaign => (
            <CampaignCard key={campaign._id}>
              <CampaignHeader>
                <CampaignName>{campaign.name}</CampaignName>
                <StatusBadge active={campaign.isActive}>
                  {campaign.isActive ? 'Activa' : 'Inactiva'}
                </StatusBadge>
              </CampaignHeader>

              <CampaignInfo>
                <InfoItem>
                  <Label>Fecha inicio:</Label>
                  <Value>{new Date(campaign.startDate).toLocaleDateString()}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Fecha fin:</Label>
                  <Value>{new Date(campaign.endDate).toLocaleDateString()}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Premios:</Label>
                  <Value>{campaign.prizes.length}</Value>
                </InfoItem>
              </CampaignInfo>

              <Actions>
                <ActionButton onClick={() => navigate(`/pmpl/admin/campaigns/${campaign._id}/edit`)}>
                  <FaEdit /> Editar
                </ActionButton>
                <ActionButton danger onClick={() => handleDelete(campaign._id)}>
                  <FaTrash /> Eliminar
                </ActionButton>
                <ResetButton onClick={() => handleReset(campaign)}>
                  <FaUndo /> Resetear
                </ResetButton>
                <BusinessButton onClick={() => showBusinessCredentials(campaign)}>
                  <FaStore /> Dashboard
                </BusinessButton>
              </Actions>
            </CampaignCard>
          ))}
        </CampaignsGrid>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #ffed4a;
  }
`;

const CampaignsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CampaignCard = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CampaignHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CampaignName = styled.h3`
  color: #fff;
  margin: 0;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  background: ${props => props.active ? '#34D399' : '#EF4444'};
  color: white;
`;

const CampaignInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const Label = styled.span`
  color: #999;
`;

const Value = styled.span`
  color: #fff;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 215, 0, 0.1)'};
  color: ${props => props.danger ? '#EF4444' : '#ffd700'};

  &:hover {
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 215, 0, 0.2)'};
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const ResetButton = styled(ActionButton)`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const BusinessButton = styled(ActionButton)`
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;

  &:hover {
    background: rgba(59, 130, 246, 0.2);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #fff;
  padding: 2rem;
`;

// Estilos globales para el modal de Sweetalert2
const GlobalStyle = createGlobalStyle`
  .business-credentials-popup {
    background: linear-gradient(135deg, 
      rgba(88, 28, 135, 0.95) 0%,
      rgba(67, 20, 102, 0.95) 100%
    ) !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    border-radius: 20px !important;
    padding: 2rem !important;
  }

  .business-credentials-title {
    color: #3b82f6 !important;
    font-size: 1.5rem !important;
  }

  .business-credentials-content {
    color: #fff !important;
    font-size: 1.1rem !important;
    
    p {
      background: rgba(59, 130, 246, 0.1);
      padding: 0.75rem;
      border-radius: 8px;
      border: 1px solid rgba(59, 130, 246, 0.2);
    }
  }

  .business-credentials-button {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #3b82f6 !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    padding: 0.75rem 2rem !important;
    font-size: 1.1rem !important;
    border-radius: 8px !important;

    &:hover {
      background: rgba(59, 130, 246, 0.2) !important;
    }
  }
`;

export default CampaignsList;